import styled from 'styled-components';
import { fmtCoord, isRoundElement, type GeoScheme } from '@entities/geodesy';
import { Muted } from '@widgets/GeoSchemes';

const VW = 680;
const VH_MAX = 460;
const VH_MIN = 220;
const PAD = 28;

const Figure = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Svg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};

  .design {
    fill: none;
    stroke: ${({ theme }) => theme.colors.text.secondary};
    stroke-width: 1.25;
  }
  /* Круглая конструкция — её проектная окружность. */
  .design-round {
    fill: none;
    stroke: ${({ theme }) => theme.colors.text.secondary};
    stroke-width: 1.25;
  }
  .design-label {
    fill: ${({ theme }) => theme.colors.text.secondary};
    font-family: ${({ theme }) => theme.font.mono};
    font-size: 11px;
  }
  .survey {
    fill: ${({ theme }) => theme.colors.status.info};
  }
  .survey.top {
    fill: ${({ theme }) => theme.colors.backgroundElevated};
    stroke: ${({ theme }) => theme.colors.status.info};
    stroke-width: 1.5;
  }
  /* Не элемент (репер, станция) — треугольник пункта, в сличение не идёт. */
  .station {
    fill: none;
    stroke: ${({ theme }) => theme.colors.text.secondary};
    stroke-width: 1.25;
    stroke-linejoin: round;
  }
  .link {
    stroke: ${({ theme }) => theme.colors.status.success};
    stroke-width: 1.5;
  }
  .link.bad {
    stroke: ${({ theme }) => theme.colors.status.danger};
    stroke-width: 2;
  }
  .axis {
    fill: ${({ theme }) => theme.colors.text.secondary};
    font-family: ${({ theme }) => theme.font.family};
    font-size: 12px;
  }
  .north {
    stroke: ${({ theme }) => theme.colors.text.primary};
    stroke-width: 1.5;
    fill: none;
  }
  .scalebar {
    stroke: ${({ theme }) => theme.colors.text.primary};
    stroke-width: 2;
  }
`;

const Legend = styled.figcaption`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(4)}`};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Key = styled.span<{ $kind: 'design' | 'survey' | 'ok' | 'bad' | 'station' }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};

  &::before {
    content: '';
    display: inline-block;
    width: ${({ $kind }) => ($kind === 'ok' || $kind === 'bad' ? '16px' : '9px')};
    height: ${({ $kind }) => ($kind === 'ok' || $kind === 'bad' ? '2px' : '9px')};
    border-radius: ${({ $kind }) => ($kind === 'survey' ? '50%' : '0')};
    border: ${({ theme, $kind }) =>
      $kind === 'design' ? `1.25px solid ${theme.colors.text.secondary}` : 'none'};
    clip-path: ${({ $kind }) => ($kind === 'station' ? 'polygon(50% 0, 100% 100%, 0 100%)' : 'none')};
    background: ${({ theme, $kind }) =>
      $kind === 'survey'
        ? theme.colors.status.info
        : $kind === 'ok'
          ? theme.colors.status.success
          : $kind === 'bad'
            ? theme.colors.status.danger
            : $kind === 'station'
              ? theme.colors.text.secondary
              : 'transparent'};
  }
`;

/** Треугольник пункта (вершина вверх) с центром в точке. */
function stationPath(x: number, y: number, r: number): string {
  return `M ${x} ${y - r} L ${x + r * 0.87} ${y + r * 0.5} L ${x - r * 0.87} ${y + r * 0.5} Z`;
}

/** «Круглая» длина масштабной линейки: 1, 2, 5 × 10ⁿ м. */
function niceLength(maxMeters: number): number {
  const p = 10 ** Math.floor(Math.log10(maxMeters));
  for (const m of [5, 2, 1]) if (m * p <= maxMeters) return m * p;
  return p;
}

/**
 * Мини-план: проектные элементы и точки съёмки в одной системе (X — вверх,
 * Y — вправо), после расчёта — линии сопоставления «проект → факт». Нужен, чтобы
 * сразу увидеть: всё легло куда надо, ничего не перепутано (X/Y, порядок колонок).
 */
export function MiniPlan({ scheme }: { scheme: GeoScheme }) {
  const design = scheme.design.elements.filter((e) => Number.isFinite(e.x) && Number.isFinite(e.y));
  const survey = scheme.survey.points.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  const links = (scheme.deviations?.items ?? []).filter((d) => d.actual);
  // Радиус круглой конструкции, м (у прочих элементов — 0).
  const radius = (e: (typeof design)[number]) => (isRoundElement(e) ? (e.size_mm ?? 0) / 2000 : 0);

  const all = [
    ...design.flatMap((e) => {
      const r = radius(e);
      return [
        { n: e.x - r, e: e.y - r },
        { n: e.x + r, e: e.y + r },
      ];
    }),
    ...survey.map((p) => ({ n: p.x, e: p.y })),
  ];
  if (all.length === 0) {
    return <Muted>План появится, когда будут проектные элементы или точки съёмки.</Muted>;
  }

  let minE = Math.min(...all.map((p) => p.e));
  let maxE = Math.max(...all.map((p) => p.e));
  let minN = Math.min(...all.map((p) => p.n));
  let maxN = Math.max(...all.map((p) => p.n));
  // Вырожденный разброс (одна точка, одна линия) — минимум метр по каждой оси.
  if (maxE - minE < 1) {
    const c = (minE + maxE) / 2;
    minE = c - 0.5;
    maxE = c + 0.5;
  }
  if (maxN - minN < 1) {
    const c = (minN + maxN) / 2;
    minN = c - 0.5;
    maxN = c + 0.5;
  }
  const spanE = maxE - minE;
  const spanN = maxN - minN;
  let s = (VW - 2 * PAD) / spanE;
  let vh = spanN * s + 2 * PAD;
  if (vh > VH_MAX) {
    s = (VH_MAX - 2 * PAD) / spanN;
    vh = VH_MAX;
  }
  vh = Math.max(vh, VH_MIN);
  const offX = (VW - spanE * s) / 2;
  const offY = (vh - spanN * s) / 2;
  const px = (e: number) => offX + (e - minE) * s;
  const py = (n: number) => offY + (maxN - n) * s;

  const showLabels = design.length <= 80;
  const bar = niceLength(spanE / 4);
  const exceeded = links.filter((l) => l.status === 'exceeded').length;
  const stations = survey.filter((p) => p.role === 'ignore').length;

  return (
    <Figure>
      <Svg
        viewBox={`0 0 ${VW} ${vh}`}
        role="img"
        aria-label={`План: проектных элементов ${design.length}, точек съёмки ${survey.length}, сопоставлено ${links.length}, с превышением ${exceeded}`}
      >
        {links.map((l) =>
          l.actual ? (
            <line
              key={`l-${l.element_id}`}
              className={l.status === 'exceeded' ? 'link bad' : 'link'}
              x1={px(l.design.y)}
              y1={py(l.design.x)}
              x2={px(l.actual.y)}
              y2={py(l.actual.x)}
            />
          ) : null,
        )}
        {design.map((e) => (
          <g key={`d-${e.id}`}>
            {radius(e) > 0 && (
              <circle className="design-round" cx={px(e.y)} cy={py(e.x)} r={radius(e) * s} />
            )}
            <rect className="design" x={px(e.y) - 4.5} y={py(e.x) - 4.5} width={9} height={9}>
              <title>
                {e.label}: X {fmtCoord(e.x)}, Y {fmtCoord(e.y)}
              </title>
            </rect>
            {showLabels && (
              <text className="design-label" x={px(e.y) + 7} y={py(e.x) - 6}>
                {e.label}
              </text>
            )}
          </g>
        ))}
        {survey.map((p, i) => {
          const title = (
            <title>
              {p.name}: X {fmtCoord(p.x)}, Y {fmtCoord(p.y)}
              {p.h != null ? `, H ${fmtCoord(p.h)}` : ''}
              {p.role === 'ignore' ? ' — не элемент' : ''}
            </title>
          );
          return p.role === 'ignore' ? (
            <path key={`s-${p.name}-${i}`} className="station" d={stationPath(px(p.y), py(p.x), 5)}>
              {title}
            </path>
          ) : (
            <circle
              key={`s-${p.name}-${i}`}
              className={p.role === 'top' ? 'survey top' : 'survey'}
              cx={px(p.y)}
              cy={py(p.x)}
              r={3}
            >
              {title}
            </circle>
          );
        })}

        {/* Стрелка на север и подписи осей. */}
        <path
          className="north"
          d={`M ${VW - 22} 34 V 12 M ${VW - 27} 18 L ${VW - 22} 12 L ${VW - 17} 18`}
        />
        <text className="axis" x={VW - 26} y={48}>
          X
        </text>
        <text className="axis" x={VW - 44} y={vh - 10}>
          Y →
        </text>
        {/* Масштабная линейка. */}
        <line className="scalebar" x1={PAD} y1={vh - 14} x2={PAD + bar * s} y2={vh - 14} />
        <text className="axis" x={PAD} y={vh - 20}>
          {fmtCoord(bar, bar < 1 ? 1 : 0)} м
        </text>
      </Svg>
      <Legend>
        <Key $kind="design">проектный элемент</Key>
        <Key $kind="survey">точка съёмки (полый кружок — верх)</Key>
        {stations > 0 && <Key $kind="station">не элемент — репер, станция</Key>}
        {links.length > 0 && (
          <>
            <Key $kind="ok">сопоставление в допуске</Key>
            <Key $kind="bad">с превышением</Key>
          </>
        )}
      </Legend>
    </Figure>
  );
}
